/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import type { Tool } from './llm-provider'

export interface FileToolAction {
  type: 'create_file' | 'update_file' | 'delete_file' | 'rename_file'
  path?: string
  content?: string
  oldPath?: string
  newPath?: string
  oldContent?: string
  newContent?: string
}

export interface FileToolContext {
  currentProject: {
    files: Record<string, string>
    isRepo?: boolean
  }
  onConfirmationRequired?: (action: FileToolAction) => Promise<boolean>
  onProgress?: (message: string) => void
}

/**
 * Get file operation tools for LLM function calling
 */
export function getFileTools(): Tool[] {
  return [
    {
      type: 'function',
      function: {
        name: 'create_file',
        description: 'Create a new file in the project with the specified content. Use this when the user asks to create a new file, add a new component, or generate new code. The user will be asked to confirm before the file is created.',
        parameters: {
          type: 'object',
          properties: {
            path: {
              type: 'string',
              description: 'The file path relative to the project root (e.g., "src/components/Button.tsx")',
            },
            content: {
              type: 'string',
              description: 'The complete content of the file to create',
            },
          },
          required: ['path', 'content'],
        },
      },
    },
    {
      type: 'function',
      function: {
        name: 'update_file',
        description: 'Update an existing file in the project. Use this when the user asks to modify, edit, or change an existing file. Provide the complete new content for the file. The user will be asked to confirm before the file is updated.',
        parameters: {
          type: 'object',
          properties: {
            path: {
              type: 'string',
              description: 'The file path relative to the project root',
            },
            content: {
              type: 'string',
              description: 'The complete new content for the file',
            },
          },
          required: ['path', 'content'],
        },
      },
    },
    {
      type: 'function',
      function: {
        name: 'delete_file',
        description: 'Delete a file from the project. Use this when the user asks to remove or delete a file. The user will be asked to confirm before the file is deleted.',
        parameters: {
          type: 'object',
          properties: {
            path: {
              type: 'string',
              description: 'The file path relative to the project root',
            },
          },
          required: ['path'],
        },
      },
    },
    {
      type: 'function',
      function: {
        name: 'rename_file',
        description: 'Rename or move a file in the project. Use this when the user asks to rename or move a file to a different location. The user will be asked to confirm before the file is renamed.',
        parameters: {
          type: 'object',
          properties: {
            old_path: {
              type: 'string',
              description: 'The current file path relative to the project root',
            },
            new_path: {
              type: 'string',
              description: 'The new file path relative to the project root',
            },
          },
          required: ['old_path', 'new_path'],
        },
      },
    },
  ]
}

/**
 * Execute a file tool call
 * Returns the result message to send back to the LLM
 */
export async function executeFileTool(
  toolName: string,
  args: any,
  context: FileToolContext
): Promise<string> {
  if (toolName === 'create_file') {
    return await executeCreateFile(args, context)
  }

  if (toolName === 'update_file') {
    return await executeUpdateFile(args, context)
  }

  if (toolName === 'delete_file') {
    return await executeDeleteFile(args, context)
  }

  if (toolName === 'rename_file') {
    return await executeRenameFile(args, context)
  }

  throw new Error(`Unknown file tool: ${toolName}`)
}

/**
 * Execute create_file tool
 */
async function executeCreateFile(
  args: { path: string; content: string },
  context: FileToolContext
): Promise<string> {
  const { path, content } = args
  const { currentProject, onConfirmationRequired, onProgress } = context

  // Check if file already exists
  if (currentProject.files[path]) {
    return `File "${path}" already exists. Use update_file to modify it, or delete it first.`
  }

  // Create the action for confirmation
  const action: FileToolAction = {
    type: 'create_file',
    path,
    content,
  }

  // Request user confirmation
  if (onConfirmationRequired) {
    const confirmed = await onConfirmationRequired(action)
    if (!confirmed) {
      return `File creation cancelled by user.`
    }
  }

  onProgress?.(`Creating file ${path}...`)

  return `Successfully created file "${path}" (${content.length} characters).`
}

/**
 * Execute update_file tool
 */
async function executeUpdateFile(
  args: { path: string; content: string },
  context: FileToolContext
): Promise<string> {
  const { path, content } = args
  const { currentProject, onConfirmationRequired, onProgress } = context

  // Check if file exists
  if (!currentProject.files[path]) {
    return `File "${path}" does not exist. Use create_file to create it first.`
  }

  const oldContent = currentProject.files[path]

  // Create the action for confirmation
  const action: FileToolAction = {
    type: 'update_file',
    path,
    oldContent,
    newContent: content,
  }

  // Request user confirmation
  if (onConfirmationRequired) {
    const confirmed = await onConfirmationRequired(action)
    if (!confirmed) {
      return `File update cancelled by user.`
    }
  }

  onProgress?.(`Updating file ${path}...`)

  return `Successfully updated file "${path}" (${oldContent.length} → ${content.length} characters).`
}

/**
 * Execute delete_file tool
 */
async function executeDeleteFile(
  args: { path: string },
  context: FileToolContext
): Promise<string> {
  const { path } = args
  const { currentProject, onConfirmationRequired, onProgress } = context

  // Check if file exists
  if (!currentProject.files[path]) {
    return `File "${path}" does not exist.`
  }

  // Create the action for confirmation
  const action: FileToolAction = {
    type: 'delete_file',
    path,
    content: currentProject.files[path],
  }

  // Request user confirmation
  if (onConfirmationRequired) {
    const confirmed = await onConfirmationRequired(action)
    if (!confirmed) {
      return `File deletion cancelled by user.`
    }
  }

  onProgress?.(`Deleting file ${path}...`)

  return `Successfully deleted file "${path}".`
}

/**
 * Execute rename_file tool
 */
async function executeRenameFile(
  args: { old_path: string; new_path: string },
  context: FileToolContext
): Promise<string> {
  const { old_path, new_path } = args
  const { currentProject, onConfirmationRequired, onProgress } = context

  // Check if source file exists
  if (!currentProject.files[old_path]) {
    return `File "${old_path}" does not exist.`
  }

  // Check if destination already exists
  if (currentProject.files[new_path]) {
    return `File "${new_path}" already exists. Delete it first or choose a different name.`
  }

  // Create the action for confirmation
  const action: FileToolAction = {
    type: 'rename_file',
    oldPath: old_path,
    newPath: new_path,
    content: currentProject.files[old_path],
  }

  // Request user confirmation
  if (onConfirmationRequired) {
    const confirmed = await onConfirmationRequired(action)
    if (!confirmed) {
      return `File rename cancelled by user.`
    }
  }

  onProgress?.(`Renaming file ${old_path} → ${new_path}...`)

  return `Successfully renamed file "${old_path}" to "${new_path}".`
}

